import React, { useCallback, useState } from 'react';
import axios from 'axios';
import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import HBox from '@/components/layout/HBox';
import VBox from '@/components/layout/VBox';
import Botao from '@/components/Botao';
import BotaoPerigoso from '@/components/BotaoPerigoso';
import DataTable, { DataTableHeader } from '@/components/layout/DataTable';
import Badge from '@/components/layout/Badge';
import Modal from '@/components/layout/Modal';
import Icon from '@mdi/react';
import { mdiLoading, mdiPencil, mdiPlus, mdiTrashCan, mdiToggleSwitch, mdiToggleSwitchOffOutline } from '@mdi/js';
import { toastError, toastSuccess } from '@/lib/toast';
import { queryClient } from '@/queryClient';
import { extractApiError } from '@/lib/apiError';
import { PolicyDTO, PolicyScope } from '@webip/shared/src/dto/firewall';
import { ApplianceDTO, DeviceGroupRefDTO, NetworkLanDTO } from '@webip/shared/src/dto/appliance';
import CampoTexto from '@/components/Formulario/CampoTexto';
import CampoSeletor from '@/components/Formulario/CampoSeletor';
import CampoMultiSeletor from '@/components/Formulario/CampoMultiSeletor';
import { PolicyEditorModal } from './PolicyEditorModal';

type ResponseList = {
    ok?: boolean;
    result: PolicyDTO[];
    total: number;
    cursor: string | number | null;
};

export function PoliciesTab() {
    const [editingPolicy, setEditingPolicy] = useState<PolicyDTO | null>(null);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [togglingId, setTogglingId] = useState<number | null>(null);

    // ── filtro de escopo (listagem) ──────────────────────────────────────────
    const [filterScope, setFilterScope] = useState<PolicyScope | ''>('');
    const [filterApplianceId, setFilterApplianceId] = useState<string | null>(null);
    const [filterGrupoId, setFilterGrupoId] = useState<string | null>(null);
    const [filterRedeId, setFilterRedeId] = useState<string | null>(null);

    // estado do formulário de criação
    const [createName, setCreateName] = useState('');
    const [createScope, setCreateScope] = useState<PolicyScope>('global');
    const [createApplianceId, setCreateApplianceId] = useState<string | null>(null);
    const [createGrupoIds, setCreateGrupoIds] = useState<string[]>([]);
    const [createRedeIds, setCreateRedeIds] = useState<string[]>([]);

    const resetCreate = () => {
        setCreateName('');
        setCreateScope('global');
        setCreateApplianceId(null);
        setCreateGrupoIds([]);
        setCreateRedeIds([]);
    };

    const queryList = useInfiniteQuery({
        queryKey: ['fw_policies', filterScope, filterApplianceId, filterGrupoId],
        queryFn: async ({ pageParam }) => {
            const params = new URLSearchParams();
            if (pageParam) params.append('c', pageParam);
            // 'global' (ou vazio) = sem restrição; só envia scopeFilter para appliance/grupos/redes
            if (filterScope === 'appliance' || filterScope === 'grupos' || filterScope === 'redes') {
                params.append('scopeFilter', filterScope);
            }
            if (filterScope === 'appliance' && filterApplianceId) params.append('applianceId', filterApplianceId);
            if (filterScope === 'grupos' && filterGrupoId) params.append('filterGrupoId', filterGrupoId);
            if (filterScope === 'redes' && filterRedeId) params.append('filterRedeId', filterRedeId);
            return await axios.get<ResponseList>(`/api/firewall/policies?${params}`, {
                withCredentials: true,
            });
        },
        getNextPageParam: lastPage => lastPage.data.cursor ?? undefined,
    });

    const queryAppliances = useQuery(['appliance-box'], async () => {
        const res = await axios.get<{ result: ApplianceDTO[] }>('/api/appliance', { withCredentials: true });
        return res.data.result;
    });

    const queryGrupos = useQuery(
        ['grupo_dispositivo_all'],
        async () => {
            const res = await axios.get<{ result: DeviceGroupRefDTO[] }>(
                `/api/grupo_dispositivo`,
                { withCredentials: true }
            );
            return res.data.result;
        }
    );

    const appliances = queryAppliances.data ?? [];
    const applianceOptions = appliances.map((a) => ({ id: String(a.id), label: a.nome }));
    const grupoOptions = (queryGrupos.data ?? []).map((g) => {
        const appNome = appliances.find((a) => a.id === g.appliance_id)?.nome ?? 'Sem appliance';
        return { id: String(g.id), label: g.nome, group: appNome };
    });
    const lanOptions = appliances.flatMap((a) =>
        (a.redesLan ?? []).map((lan: NetworkLanDTO) => ({
            id: String(lan.id),
            label: lan.nome,
            group: a.nome,
        }))
    );

    const mutCreate = useMutation(
        async () => {
            const payload: Record<string, unknown> = {
                name: createName.trim(),
                priority: 10,
                enabled: true,
                defaultAction: 'allow',
                scope: createScope,
            };
            if (createScope === 'appliance') {
                payload.applianceId = createApplianceId ? Number(createApplianceId) : null;
            } else if (createScope === 'grupos') {
                payload.grupoIds = createGrupoIds.map(Number);
            } else if (createScope === 'redes') {
                payload.redesIds = createRedeIds.map(Number);
            }
            return axios.post('/api/firewall/policies', payload, { withCredentials: true });
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_policies']);
                toastSuccess('Política criada com sucesso!');
                setIsCreateOpen(false);
                resetCreate();
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao criar política');
                toastError(message);
            },
        },
    );

    const mutDelete = useMutation(
        async (id: number) =>
            axios.delete(`/api/firewall/policies/${id}`, { withCredentials: true }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['fw_policies']);
                toastSuccess('Política removida com sucesso!');
            },
            onError: (error: unknown) => {
                const { message } = extractApiError(error, 'Erro ao remover política');
                toastError(message);
            },
        },
    );

    const handleToggle = useCallback(async (policy: PolicyDTO) => {
        if (togglingId) return;
        setTogglingId(policy.id);
        try {
            await axios.patch(`/api/firewall/policies/${policy.id}/toggle`, {}, { withCredentials: true });
            queryClient.invalidateQueries(['fw_policies']);
        } catch (error) {
            const { message } = extractApiError(error, 'Erro ao alterar estado da política');
            toastError(message);
        } finally {
            setTogglingId(null);
        }
    }, [togglingId]);

    const allPolicies = queryList.data?.pages?.flatMap(p => p.data.result ?? []) ?? [];

    const headers: DataTableHeader[] = [
        {
            label: 'Nome',
            column: 'name',
            width: '30%',
            render: (row: PolicyDTO) => (
                <VBox>
                    <span>{row.name}</span>
                    <HBox>
                        <Badge variant="info" style={{ fontSize: '10px' }}>
                            {row.scope === 'global' && 'Global'}
                            {row.scope === 'appliance' && `Appliance: ${appliances.find(a => a.id === row.applianceId)?.nome ?? row.applianceId}`}
                            {row.scope === 'grupos' && `Grupos: ${grupoOptions ? row.grupoIds.map(gid => grupoOptions.find(go => go.id === String(gid))?.label ?? gid).join(', ') : `(${row.grupoIds.length})`}`}
                            {row.scope === 'redes' && `Redes: ${lanOptions.length ? (row.redesIds ?? []).map(rid => lanOptions.find(lo => lo.id === String(rid))?.label ?? rid).join(', ') : `(${(row.redesIds ?? []).length})`}`}
                        </Badge>
                    </HBox>
                </VBox>
            ),
        },
        {
            label: 'Prioridade',
            column: 'priority',
            width: '100px',
            alignCenter: true,
        },
        {
            label: 'Ação padrão',
            width: '120px',
            alignCenter: true,
            render: (row: PolicyDTO) => (
                <Badge variant={row.defaultAction === 'allow' ? 'success' : 'danger'}>
                    {row.defaultAction}
                </Badge>
            ),
        },
        {
            label: 'Regras',
            width: '80px',
            alignCenter: true,
            render: (row: PolicyDTO) => row.rules.length,
        },
        {
            label: 'Ativo',
            width: '80px',
            alignCenter: true,
            render: (row: PolicyDTO) => (
                <Botao
                    size="sm"
                    variant="none-primary"
                    icon={row.enabled ? mdiToggleSwitch : mdiToggleSwitchOffOutline}
                    iconProps={{ color: row.enabled ? 'var(--color-success)' : 'var(--color-muted)' }}
                    disabled={togglingId === row.id}
                    onClick={() => handleToggle(row)}
                />
            ),
        },
        {
            label: 'Ações',
            width: '120px',
            stickRight: true,
            alignRight: true,
            render: (row: PolicyDTO) => (
                <HBox style={{ gap: '8px', justifyContent: 'flex-end' }}>
                    <Botao
                        size="sm"
                        variant="outline-primary"
                        icon={mdiPencil}
                        onClick={() => setEditingPolicy(row)}
                        title="Editar regras"
                    />
                    <BotaoPerigoso
                        size="sm"
                        variant="outline-danger"
                        icon={mdiTrashCan}
                        onConfirm={() => mutDelete.mutate(row.id)}
                    />
                </HBox>
            ),
        },
    ];

    return (
        <VBox style={{ gap: '16px', flex: 1, overflow: 'hidden' }}>
            <HBox style={{ justifyContent: 'flex-end' }}>
                <Botao
                    variant="primary"
                    icon={mdiPlus}
                    onClick={() => { resetCreate(); setIsCreateOpen(true); }}
                >
                    Nova Política
                </Botao>
            </HBox>

            {/* Filtro de escopo */}
            <HBox style={{ gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ minWidth: '220px' }}>
                    <CampoSeletor
                        label="Escopo"
                        noSearch
                        nullable
                        placeholder="Todos (padrão)"
                        value={filterScope || null}
                        onChange={(v) => {
                            setFilterScope((v ?? '') as PolicyScope | '');
                            setFilterApplianceId(null);
                            setFilterGrupoId(null);
                            setFilterRedeId(null);
                        }}
                        options={[
                            { id: 'global', label: 'Todos' },
                            { id: 'appliance', label: 'Appliance específico' },
                            { id: 'grupos', label: 'Grupo de dispositivos' },
                            { id: 'redes', label: 'Rede LAN' },
                        ]}
                    />
                </div>
                {filterScope === 'appliance' && (
                    <div style={{ minWidth: '220px' }}>
                        <CampoSeletor
                            label="Appliance"
                            value={filterApplianceId}
                            onChange={setFilterApplianceId}
                            options={applianceOptions}
                            nullable
                            placeholder="Todos os appliances"
                        />
                    </div>
                )}
                {filterScope === 'grupos' && (
                    <div style={{ minWidth: '220px' }}>
                        <CampoSeletor
                            label="Grupo de dispositivos"
                            value={filterGrupoId}
                            onChange={setFilterGrupoId}
                            options={grupoOptions}
                            nullable
                            placeholder="Todos os grupos"
                        />
                    </div>
                )}
                {filterScope === 'redes' && (
                    <div style={{ minWidth: '220px' }}>
                        <CampoSeletor
                            label="Rede LAN"
                            value={filterRedeId}
                            onChange={setFilterRedeId}
                            options={lanOptions}
                            nullable
                            placeholder="Todas as redes"
                        />
                    </div>
                )}
            </HBox>

            {queryList.isLoading && (
                <HBox style={{ justifyContent: 'center', padding: '40px' }}>
                    <Icon path={mdiLoading} spin size={2} />
                </HBox>
            )}

            {queryList.isError && (
                <p style={{ color: 'var(--color-danger)' }}>Erro ao carregar políticas. Tente novamente.</p>
            )}

            {!queryList.isLoading && !queryList.isError && (
                <DataTable
                    cabecalhos={headers}
                    linhas={allPolicies}
                    onLoadMore={() => {
                        if (queryList.hasNextPage && !queryList.isFetchingNextPage) {
                            queryList.fetchNextPage();
                        }
                    }}
                    hasMoreData={queryList.hasNextPage}
                    isLoading={queryList.isFetchingNextPage}
                />
            )}

            {/* Modal criação */}
            <Modal
                show={isCreateOpen}
                title="Nova Política"
                onClose={() => setIsCreateOpen(false)}
                onCancel={() => setIsCreateOpen(false)}
                onConfirm={() => mutCreate.mutate()}
                confirmProps={{ label: 'Criar', disabled: mutCreate.isLoading || !createName.trim() || (createScope === 'appliance' && !createApplianceId) || (createScope === 'redes' && createRedeIds.length === 0) }}
                cancelProps={{ label: 'Cancelar' }}
            >
                <VBox style={{ gap: '12px' }}>
                    <CampoTexto
                        label="Nome da política"
                        value={createName}
                        onChange={setCreateName}
                        autoFocus
                        placeholder="Ex: Bloquear redes sociais"
                    />
                    <CampoSeletor
                        label="Escopo"
                        noSearch
                        value={createScope}
                        onChange={(v) => { setCreateScope(v as PolicyScope); setCreateApplianceId(null); setCreateGrupoIds([]); setCreateRedeIds([]); }}
                        options={[
                            { id: 'global', label: 'Global (todos os appliances)' },
                            { id: 'appliance', label: 'Appliance específico' },
                            { id: 'grupos', label: 'Grupos de dispositivos' },
                            { id: 'redes', label: 'Rede LAN' },
                        ]}
                    />
                    {(createScope === 'appliance') && (
                        <CampoSeletor
                            label="Appliance"
                            value={createApplianceId}
                            onChange={setCreateApplianceId}
                            options={applianceOptions}
                        />
                    )}
                    {createScope === 'grupos' && (
                        <CampoMultiSeletor
                            label="Grupos de dispositivos"
                            value={createGrupoIds}
                            onChange={setCreateGrupoIds}
                            options={grupoOptions}
                        />
                    )}
                    {createScope === 'redes' && (
                        <CampoMultiSeletor
                            label="Redes LAN"
                            value={createRedeIds}
                            onChange={setCreateRedeIds}
                            options={lanOptions}
                        />
                    )}
                </VBox>
            </Modal>

            {/* Modal edição de regras */}
            {editingPolicy && (
                <PolicyEditorModal
                    policy={editingPolicy}
                    onClose={() => setEditingPolicy(null)}
                />
            )}
        </VBox>
    );
}
